import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Switch } from '@connect-x-app/hybrid-ui';
import { PushNotificationGroup, PushNotificationSetting } from '@connect-x-app/shared-domain-models';
import {
  getBasePushNotificationKey,
  getPushNotificationGroupState,
  getPushNotificationSettingsForGroup,
} from '@connect-x-app/shared-utils';
import { APP_CONFIG_TEST_IDS } from '@connect-x-app/web-test-ids';
import { BlockGroup, BlockRow } from '@connect-x-app/web-ui';

type Props = {
  group: PushNotificationGroup;
  isUpdating: boolean;
  onToggleAllPushNotifications: (group: PushNotificationGroup, isEnabled: boolean) => void;
  onUpdatePushNotificationSetting: (key: PushNotificationSetting['key'], isEnabled: boolean) => void;
  pushNotifications: PushNotificationSetting[];
};

export const PushNotificationGroupDetail = ({
  group,
  isUpdating,
  onToggleAllPushNotifications,
  onUpdatePushNotificationSetting,
  pushNotifications,
}: Props) => {
  const { t } = useTranslation();

  const groupSettings = useMemo(() => getPushNotificationSettingsForGroup(pushNotifications, group), [group, pushNotifications]);

  // Derived, never persisted: OFF only when every setting in the group is off, so CUSTOM still reads as allowed.
  const isGroupAllowed = getPushNotificationGroupState(pushNotifications, group) !== 'OFF';
  const isMentionsEnabled = groupSettings.some(
    setting => getBasePushNotificationKey(setting.key) === 'MENTIONS' && setting.isEnabled,
  );
  // Only the master's own write covers the whole group, so a single row toggling must not grey it out.
  const isGroupWriteInFlight = isUpdating && groupSettings.every(setting => !!setting.isUpdating);

  const renderSetting = (pushSetting: PushNotificationSetting) => {
    // Compare on the base key: the same row exists in both groups under a different literal.
    const isUserMessagesSetting = getBasePushNotificationKey(pushSetting.key) === 'USERS';
    const isGatedByMentions = isUserMessagesSetting && !isMentionsEnabled;
    const isDisabled = (isUpdating && !!pushSetting.isUpdating) || isGatedByMentions;

    return (
      <BlockRow
        indented={isUserMessagesSetting}
        isDisabled={isDisabled}
        key={pushSetting.key}
        label={t(`SETTINGS.${getBasePushNotificationKey(pushSetting.key)}`)}
        labelIcon={isGatedByMentions ? 'circularInfoOutline' : undefined}
        testId={APP_CONFIG_TEST_IDS.settings.setting(pushSetting.key)}
        tooltip={isGatedByMentions ? t('SETTINGS.TOOLTIP') : undefined}
        trailing={
          <Switch
            isDisabled={isDisabled}
            onChange={isEnabled => onUpdatePushNotificationSetting(pushSetting.key, isEnabled)}
            size="s"
            testId={APP_CONFIG_TEST_IDS.settings.itemToggle(pushSetting.key)}
            value={pushSetting.isEnabled}
          />
        }
      />
    );
  };

  return (
    <>
      <BlockRow
        label={t('SETTINGS.ALLOW_PUSH_NOTIFICATIONS')}
        testId={APP_CONFIG_TEST_IDS.settings.allowPushNotificationsRow}
        trailing={
          <Switch
            isDisabled={isGroupWriteInFlight}
            onChange={isEnabled => onToggleAllPushNotifications(group, isEnabled)}
            size="s"
            testId={APP_CONFIG_TEST_IDS.settings.allowPushNotifications}
            value={isGroupAllowed}
          />
        }
      />
      {isGroupAllowed ? (
        <BlockGroup className="settings-group" testId={APP_CONFIG_TEST_IDS.settings.pushNotifications}>
          {groupSettings.map(renderSetting)}
        </BlockGroup>
      ) : null}
    </>
  );
};
